import React, { useCallback, useState } from "react";

import Form from "antd/es/form";
import Input from "antd/es/input";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { authCommonStyles as commonStyles } from "@mapmycustomers/auth";
import { Alert, Button, FormItem } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import analyticsService from "@app/util/analytic/AnalyticsService";

import styles from "./ForgotPassword.module.scss";

interface Props {
  errorMessage?: string;
  isLoading: boolean;
  onResetPassword: (email: string) => void;
}

const ForgotPasswordForm = ({ errorMessage, isLoading, onResetPassword }: Props) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const handleFormFieldsChange = useCallback(
    () => setCanSubmit(!form.getFieldsError().some((field) => field.errors.length > 0)),
    [form, setCanSubmit]
  );

  const handleSubmit = useCallback(
    (values: { email: string }) => {
      onResetPassword(values.email);
      analyticsService.clicked(["Forgot password", "Send Link"]);
    },
    [onResetPassword]
  );

  const handleGoBack = useCallback(
    () => analyticsService.clicked(["Forgot password", "Go back"]),
    []
  );

  return (
    <div className={commonStyles.container}>
      {errorMessage && <Alert message={errorMessage} type="error" />}
      <Form
        form={form}
        initialValues={{
          remember: true,
        }}
        layout="vertical"
        name="forgotPassword"
        onFieldsChange={handleFormFieldsChange}
        onFinish={handleSubmit}
        preserve={false}
      >
        <h1 className={commonStyles.title}>
          <FormattedMessage
            id="auth.forgotPassword.form.title"
            defaultMessage="Access your MMC Account"
            description="Title of forgot password card"
          />
        </h1>
        <p className={commonStyles.description}>
          <FormattedMessage
            id="auth.forgotPassword.form.description"
            defaultMessage="We’ll send you a link to set a new password"
            description="Description of the forgot password card"
          />
        </p>
        <FormItem
          colon={false}
          label={intl.formatMessage({
            id: "auth.forgotPassword.form.email",
            defaultMessage: "Email",
            description: "Title of the email field on the forgotPassword form",
          })}
          name="email"
          requiredMark="optional"
          rules={[
            {
              message: intl.formatMessage({
                id: "auth.forgotPassword.form.email.required",
                defaultMessage: "Please input your email!",
                description: "Field is email error message for the username field",
              }),
              required: true,
            },
            { type: "email" },
          ]}
        >
          <Input autoComplete="username" name="username" size="large" type="email" />
        </FormItem>
        <FormItem>
          <Button
            className={styles.resetButton}
            disabled={!canSubmit || isLoading}
            htmlType="submit"
            loading={isLoading}
            type="primary"
          >
            {isLoading
              ? intl.formatMessage({
                  id: "auth.forgotPassword.form.submit.loading",
                  defaultMessage: "Sending Link...",
                  description: "Title of the submit login form button after it has been pressed",
                })
              : intl.formatMessage({
                  id: "auth.forgotPassword.form.submit",
                  defaultMessage: "Send Link",
                  description: "Title of the submit forgot password form button",
                })}
          </Button>
        </FormItem>
        <div className={styles.backToLoginLink}>
          <Link onClick={handleGoBack} to={Path.LOGIN}>
            {intl.formatMessage({
              id: "auth.forgotPassword.form.loginLink",
              defaultMessage: "Go back",
              description: "Title of the Back to Login Link",
            })}
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default ForgotPasswordForm;
